import { createBerthCallChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/BerthCallChangeDataFactory';
import { createCargoOperationChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoOperationChangeDataFactory';
import { createPortCallChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/RotationPortCallChangeDataFactory';
import { createDeleteStowagePlanChange, createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import {
    compareBerthCalls,
    compareCargoes,
    compareCargoOperations,
    comparePortCalls,
    setIgnoredMergePropertiesInChangeData,
} from './comparisonUtils';
import { getChangeDataGuid, setChangeDataGuid } from '../stowagePlanChangeUtils/stowageChangeDataTypeUtils';
import {
    findByPortCallId,
    findByBerthCallId,
    findByCargoOperationId,
    RotationItem,
    getItems,
    getItemImosId,
    findByCargoId,
    RotationElement,
    findByPortCallMergeProperties,
    findByBerthCallMergeProperties,
    hasMatchingSpiltCargoOperations,
} from './itemFindersUtils';
import { createCargoChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoChangeDataFactory';
import { isManualCall, isVoyageClosedOrCompleted } from '../stowageRotationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { UUID } from 'crypto';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';

interface ComparisonData {
    stowagePlanGuid: UUID;
    stowagePlanTransitOperations: RotationPortCall[];
    existingRotation: RotationPortCall[];
    incomingRotation: RotationPortCall[];
    existingCargoes: RotationCargo[];
    incomingCargoes: RotationCargo[];
}
export const getNumberOfClosedSyncChanges = (changes: StowagePlanChange[]): number =>
    getNumberOfChanges(filterSyncChangesByOpenOrClosed(changes, true));
export const getNumberOfOpenSyncChanges = (changes: StowagePlanChange[]): number =>
    getNumberOfChanges(filterSyncChangesByOpenOrClosed(changes, false));

export const getNumberOfChanges = (changes: StowagePlanChange[]): number =>
    changes.reduce((acc, change) => {
        const descriptions = JSON.parse(change.data ?? '{}')?.extraData?.descriptions;
        return acc + (descriptions?.length ?? 1);
    }, 0);
export const filterSyncChangesByOpenOrClosed = (changes: StowagePlanChange[], shouldBeClosedOrCompleted: boolean) => {
    return changes.filter((change) => {
        if (!change.data) return false;
        const dataJson = change.data;
        const data = dataJson ? JSON.parse(dataJson) : null;

        return shouldBeClosedOrCompleted ? !data.extraData?.isChangeInOpenVoyage : data.extraData?.isChangeInOpenVoyage;
    });
};

export const compareRotations = (comparisonData: ComparisonData): StowagePlanChange[] => {
    // For cargoes and cargo operations, we pass a finder that always returns null to disable auto-merging.
    // Manual cargoes/cargo operations should remain separate from IMOS cargoes/cargo operations,
    // allowing users to manually merge them via the context menu when appropriate.
    const noAutoMergeForCargoes = () => null;

    return [
        ...checkItems(
            comparisonData,
            findByPortCallId,
            findByPortCallMergeProperties,
            StowageChangeDataType.RotationPortCall,
            createPortCallChangeData,
            comparePortCalls
        ),
        ...checkItems(
            comparisonData,
            findByBerthCallId,
            findByBerthCallMergeProperties,
            StowageChangeDataType.RotationBerthCall,
            createBerthCallChangeData,
            compareBerthCalls
        ),
        ...checkItems(
            comparisonData,
            findByCargoId,
            noAutoMergeForCargoes,
            StowageChangeDataType.RotationCargo,
            createCargoChangeData,
            compareCargoes
        ),
        ...checkItems(
            comparisonData,
            findByCargoOperationId,
            noAutoMergeForCargoes,
            StowageChangeDataType.RotationCargoOperation,
            createCargoOperationChangeData,
            compareCargoOperations
        ),
    ];
};

type ChangeOperationType = 'Added' | 'Deleted' | 'Merged' | undefined;

const getDescriptor = (changeDataType: StowageChangeDataType, changeElement?: RotationElement, operationType?: ChangeOperationType) => {
    const operationPrefix = operationType ? `${operationType} ` : '';

    if (changeDataType === StowageChangeDataType.RotationPortCall) return `${operationPrefix}Port Call`;
    if (changeDataType === StowageChangeDataType.RotationBerthCall) return `${operationPrefix}Berth Call`;
    if (changeDataType === StowageChangeDataType.RotationCargoOperation) {
        const cargoOperation = changeElement as RotationCargoOperation;
        return `${operationPrefix}Cargo Operation ${cargoOperation.operationType ?? ''} ${cargoOperation.contractWeight}MT`;
    }
    if (changeDataType === StowageChangeDataType.RotationCargo) return `${operationPrefix}Cargo`;

    return '';
};

const checkItems = (
    comparisonData: ComparisonData,
    findItemById: (id: number, list: RotationPortCall[] | RotationCargo[]) => RotationElement,
    findItemByMergeProperties: (
        rotationElement: RotationElement,
        existingList: RotationPortCall[] | RotationCargo[],
        extraArgs?: any
    ) => RotationElement,
    changeDataType: StowageChangeDataType,
    createChangeData?: (item: RotationElement) => Partial<RotationElement>,
    compareItems?: (stowagePlanGuid: UUID, existingItem: RotationItem, incomingItem: RotationItem) => StowagePlanChange | null
): StowagePlanChange[] => {
    const existingItems = getItems(
        changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation,
        changeDataType
    );
    const incomingItems = getItems(
        changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.incomingCargoes : comparisonData.incomingRotation,
        changeDataType
    );

    setSyncStatus(comparisonData, changeDataType, existingItems, incomingItems);
    assignCorrectGuids(changeDataType, incomingItems, comparisonData);
    const mergedItems = getMergedItems(
        incomingItems,
        findItemByMergeProperties,
        comparisonData,
        changeDataType,
        comparisonData.stowagePlanTransitOperations,
        createChangeData
    );

    return [
        ...mergedItems,
        ...getDeletedItems(existingItems, findItemById, comparisonData, changeDataType, mergedItems),
        ...getAddedItems(
            incomingItems,
            findItemById,
            comparisonData,
            changeDataType,
            comparisonData.stowagePlanTransitOperations,
            createChangeData,
            mergedItems
        ),
        ...getUpdatedItems(existingItems, incomingItems, findItemById, comparisonData, changeDataType, compareItems),
    ];
};

const assignCorrectGuids = (changeDataType: StowageChangeDataType, incomingItems: RotationItem[], comparisonData: ComparisonData): void => {
    incomingItems.forEach((incomingItems) => {
        const incomingElement = incomingItems.rotationElement;
        switch (changeDataType) {
            case StowageChangeDataType.RotationBerthCall: {
                const berthCall = incomingElement as RotationBerthCall;
                const existingPortCall = comparisonData.existingRotation.find(
                    (portCall) => portCall.imosPortCallId === berthCall.portCall?.imosPortCallId
                );
                if (existingPortCall) berthCall.portCallGuid = existingPortCall.portCallGuid;
                break;
            }
            case StowageChangeDataType.RotationCargoOperation: {
                const cargoOp = incomingElement as RotationCargoOperation;
                const existingPortCall = comparisonData.existingRotation.find(
                    (portCall) => portCall.imosPortCallId === cargoOp.berthCall?.portCall?.imosPortCallId
                );
                const existingBerthCall = comparisonData.existingRotation
                    .flatMap((pc) => pc.berthCalls)
                    .find((bc) => bc.imosBerthCallId === cargoOp.berthCall?.imosBerthCallId);

                if (existingPortCall) cargoOp.portCallGuid = existingPortCall.portCallGuid;
                cargoOp.berthCallGuid =
                    existingBerthCall?.berthCallGuid ??
                    comparisonData.incomingRotation
                        .flatMap((pc) => pc.berthCalls)
                        .find((bc) => bc.imosBerthCallId === cargoOp.berthCall?.imosBerthCallId)?.berthCallGuid;

                if (cargoOp.cargo) {
                    const existingCargo = comparisonData.existingCargoes.find((c) => c.imosCargoId === cargoOp.cargo?.imosCargoId);
                    if (existingCargo && existingCargo.cargoGuid !== cargoOp.cargoGuid) {
                        cargoOp.cargo = existingCargo;
                        cargoOp.cargoGuid = existingCargo.cargoGuid;
                    }
                }
                break;
            }
            case StowageChangeDataType.RotationCargo: {
                const cargo = incomingElement as RotationCargo;
                const existingCargo = comparisonData.existingCargoes.find((c) => c.imosCargoId === cargo.imosCargoId);
                if (existingCargo) cargo.cargoGuid = existingCargo.cargoGuid;
                break;
            }
        }
    });
};

const setSyncStatus = (
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    // TODO: NAMING-CONVENTION - Parameter has typo 'exisitingItems' should be 'existingItems'
    exisitingItems: RotationItem[],
    incomingItems: RotationItem[]
) => {
    let openPortCall = comparisonData.existingRotation
        .sort((a, b) => +a.voyageNumber - +b.voyageNumber)
        .find((pc) => !isVoyageClosedOrCompleted(pc.voyageStatus) || pc.portCallStatus !== PortCallStatusEnum.Departed);

    if (!openPortCall) {
        openPortCall = comparisonData.incomingRotation
            .sort((a, b) => +a.voyageNumber - +b.voyageNumber)
            .find((pc) => !isVoyageClosedOrCompleted(pc.voyageStatus) || pc.portCallStatus !== PortCallStatusEnum.Departed);
    }

    if (!openPortCall) return;

    const isVoyageOpen = (portCall: RotationPortCall) =>
        !isVoyageClosedOrCompleted(portCall.voyageStatus) ||
        +portCall.voyageNumber >= +openPortCall.voyageNumber ||
        (portCall.turnaroundVoyageNumber && +portCall.turnaroundVoyageNumber >= +openPortCall.voyageNumber);

    const findAssociatedPortCall = (item: RotationItem): RotationPortCall | undefined => {
        switch (changeDataType) {
            case StowageChangeDataType.RotationPortCall:
                return item.rotationElement as RotationPortCall;
            case StowageChangeDataType.RotationBerthCall: {
                const berthCall = item.rotationElement as RotationBerthCall;
                return (
                    comparisonData.existingRotation.find((pc) => pc.berthCalls.some((bc) => bc.berthCallGuid === berthCall.berthCallGuid)) ||
                    berthCall.portCall
                );
            }
            case StowageChangeDataType.RotationCargoOperation: {
                const cargoOperation = item.rotationElement as RotationCargoOperation;
                return (
                    comparisonData.existingRotation.find((pc) => pc.portCallGuid === cargoOperation.portCallGuid) ||
                    cargoOperation.berthCall?.portCall
                );
            }
            case StowageChangeDataType.RotationCargo: {
                const cargo = item.rotationElement as RotationCargo;
                const incomingPortCall = comparisonData.incomingRotation
                    .flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations))
                    .find((co) => co.cargoGuid === cargo.cargoGuid)?.berthCall?.portCall;
                return incomingPortCall;
            }
        }
    };

    const markItemIfInOpenVoyage = (item: RotationItem) => {
        const portCall = findAssociatedPortCall(item);
        item.extraData = { ...item.extraData, voyageNumber: portCall?.voyageNumber };
        if (
            (portCall && isVoyageOpen(portCall)) ||
            (!portCall && (item.rotationElement as any).voyageNumber && (item.rotationElement as any).voyageNumber >= openPortCall.voyageNumber)
        ) {
            item.extraData = { ...item.extraData, isChangeInOpenVoyage: true };
        } else if (
            changeDataType === StowageChangeDataType.RotationCargo &&
            (item.rotationElement as RotationCargo).voyageNumber >= openPortCall.voyageNumber
        ) {
            item.extraData = { ...item.extraData, isChangeInOpenVoyage: true, isRogueCargo: true };
        }
    };

    // Mark existing items
    // TODO: NAMING-CONVENTION - Parameter has typo 'exisitingItems' should be 'existingItems'
    exisitingItems.forEach(markItemIfInOpenVoyage);

    // Mark incoming items
    incomingItems.forEach(markItemIfInOpenVoyage);

    // For existing items that have corresponding incoming items, ensure they get the correct status
    // based on the incoming item's voyage status
    // TODO: NAMING-CONVENTION - Parameter has typo 'exisitingItems' should be 'existingItems'
    exisitingItems.forEach((existingItem) => {
        const correspondingIncomingItem = incomingItems.find(
            (incomingItem) =>
                getItemImosId(changeDataType, existingItem.rotationElement) === getItemImosId(changeDataType, incomingItem.rotationElement)
        );

        if (correspondingIncomingItem && correspondingIncomingItem.extraData?.isChangeInOpenVoyage) {
            existingItem.extraData = {
                ...existingItem.extraData,
                isChangeInOpenVoyage: true,
                voyageNumber: correspondingIncomingItem.extraData.voyageNumber,
            };
        }
    });
};

const getMergedItems = (
    incomingItems: RotationItem[],
    findItemByMergeProperties: (
        rotationElement: RotationElement,
        incomingList: RotationPortCall[] | RotationCargo[],
        extraArgs?: any
    ) => RotationElement,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    transitOperations: RotationPortCall[],
    createChangeData: (item: RotationElement) => Partial<RotationElement>
): StowagePlanChange[] => {
    return incomingItems
        .filter((incomingItem) =>
            findItemByMergeProperties(
                incomingItem.rotationElement,
                changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation,
                transitOperations
            )
        )
        .map((incomingItem) => {
            const changeData = createChangeData(incomingItem.rotationElement) as any;

            changeData.extraData = {
                ...incomingItem.extraData,
                descriptions: [`${getDescriptor(changeDataType, incomingItem.rotationElement, 'Merged')}`],
            };

            const existingItem = findItemByMergeProperties(
                incomingItem.rotationElement,
                changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation,
                transitOperations
            );

            const changeDataGuid = getChangeDataGuid(changeDataType, existingItem);
            setChangeDataGuidByType(changeDataType, incomingItem.rotationElement, changeDataGuid, comparisonData);
            setIgnoredMergePropertiesInChangeData(changeData, changeDataType, existingItem);

            return createStowagePlanChange(comparisonData.stowagePlanGuid, changeDataGuid, changeDataType, changeData, true);
        });
};

const setChangeDataGuidByType = (
    changeDataType: StowageChangeDataType,
    incomingElement: RotationElement,
    changeDataGuid: UUID,
    comparisonData: ComparisonData
) => {
    setChangeDataGuid(changeDataType, incomingElement, changeDataGuid);
    switch (changeDataType) {
        case StowageChangeDataType.RotationPortCall: {
            const portCall = incomingElement as RotationPortCall;
            portCall.berthCalls.forEach((berthCall) => {
                berthCall.portCallGuid = changeDataGuid;
                berthCall.cargoOperations.forEach((cargoOperation) => {
                    cargoOperation.portCallGuid = changeDataGuid;
                });
            });
            break;
        }
        case StowageChangeDataType.RotationBerthCall: {
            const berthCall = incomingElement as RotationBerthCall;
            berthCall.cargoOperations.forEach((cargoOperation) => {
                cargoOperation.berthCallGuid = changeDataGuid;
            });
            break;
        }
        case StowageChangeDataType.RotationCargo: {
            const cargo = incomingElement as RotationCargo;
            const cargoOperationForCargo = comparisonData.incomingRotation
                .flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations))
                .find((co) => co.imosCargoId === cargo.imosCargoId);

            if (cargoOperationForCargo) {
                cargoOperationForCargo.cargoGuid = cargo.cargoGuid;
            }

            break;
        }
    }
};

const getDeletedItems = (
    existingItems: RotationItem[],
    findItemById: (id: number | UUID, list: RotationPortCall[] | RotationCargo[]) => RotationElement,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    mergedItems: StowagePlanChange[]
): StowagePlanChange[] => {
    return existingItems
        .filter(
            (existingItem) =>
                !isManualCall(existingItem.rotationElement) &&
                !findItemById(
                    getItemImosId(changeDataType, existingItem.rotationElement),
                    changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.incomingCargoes : comparisonData.incomingRotation
                ) &&
                !mergedItems.find((mergedItem) => mergedItem.changeDataGuid === getChangeDataGuid(changeDataType, existingItem.rotationElement))
        )
        .map((existingItem) => {
            const change = createDeleteStowagePlanChange(
                comparisonData.stowagePlanGuid,
                getChangeDataGuid(changeDataType, existingItem.rotationElement),
                changeDataType
            );
            change.data = JSON.stringify({
                extraData: { ...existingItem.extraData, descriptions: [`${getDescriptor(changeDataType, existingItem.rotationElement, 'Deleted')}`] },
            });
            return change;
        });
};

const getAddedItems = (
    incomingItems: RotationItem[],
    findItemById: (id: number | UUID, list: RotationPortCall[] | RotationCargo[]) => RotationElement,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    transitOperations: RotationPortCall[],
    createChangeData: (item: RotationElement) => Partial<RotationElement>,
    mergedItems: StowagePlanChange[]
): StowagePlanChange[] => {
    return incomingItems
        .filter((incomingItem) => {
            const incomingImosItemId = getItemImosId(changeDataType, incomingItem.rotationElement);
            let isAdded = !findItemById(
                incomingImosItemId,
                changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.existingCargoes : comparisonData.existingRotation
            );

            if (isAdded && mergedItems.length > 0) {
                isAdded = !mergedItems.find((mergedItem) => {
                    const data = JSON.parse(mergedItem.data);
                    const mergedImosItemId = getItemImosId(changeDataType, data);
                    return incomingImosItemId === mergedImosItemId;
                });
            }

            if (isAdded && changeDataType === StowageChangeDataType.RotationCargoOperation) {
                isAdded = !hasMatchingSpiltCargoOperations(
                    incomingItem.rotationElement as RotationCargoOperation,
                    comparisonData.existingRotation,
                    transitOperations
                );
            }

            return isAdded;
        })
        .flatMap((incomingItem) => {
            const stowagePlanChanges: StowagePlanChange[] = [];
            const changeData = createChangeData(incomingItem.rotationElement) as any;
            const incomingElement = incomingItem.rotationElement;

            changeData.extraData = { ...incomingItem.extraData, descriptions: [`${getDescriptor(changeDataType, incomingElement, 'Added')}`] };
            stowagePlanChanges.push(
                createStowagePlanChange(
                    comparisonData.stowagePlanGuid,
                    getChangeDataGuid(changeDataType, incomingItem.rotationElement),
                    changeDataType,
                    changeData,
                    true
                )
            );
            return stowagePlanChanges;
        });
};

const getUpdatedItems = (
    existingItems: RotationItem[],
    incomingItems: RotationItem[],
    findItemById: (id: number | UUID, list: RotationPortCall[] | RotationCargo[]) => RotationElement,
    comparisonData: ComparisonData,
    changeDataType: StowageChangeDataType,
    compareItems: (stowagePlanGuid: UUID, existingItem: RotationItem, incomingItem: RotationItem) => StowagePlanChange | null
): StowagePlanChange[] => {
    const updatedItems = existingItems
        .map((existingItem) => {
            const incomingElement = findItemById(
                getItemImosId(changeDataType, existingItem.rotationElement),
                changeDataType === StowageChangeDataType.RotationCargo ? comparisonData.incomingCargoes : comparisonData.incomingRotation
            );
            if (!incomingElement) return null;

            const incomingItemWithExtraData = incomingItems.find(
                (item) => getItemImosId(changeDataType, item.rotationElement) === getItemImosId(changeDataType, existingItem.rotationElement)
            );

            const incomingItem = {
                rotationElement: incomingElement,
                extraData: incomingItemWithExtraData?.extraData,
            };

            return compareItems(comparisonData.stowagePlanGuid, existingItem, incomingItem);
        })
        .filter(Boolean) as StowagePlanChange[];

    return updatedItems;
};
